import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { useMobileApp } from '../../../../hooks/useMobileApp';
import { StageComponentInterface, UiComponentElementInterface } from '../../../../interfaces/pagebuilder';
import PollNotification from '../../../common/Poll/PollNotification';
import GeneralStageVideo from '../TypeStage/GeneralVideo/GeneralVideo';
import CustomFullScreenStage from '../TypeStage/StageComponent/MbwStageComponent/CustomFullScreenStage';
import MbwMenu from '../TypeStage/StageComponent/MbwStageComponent/MbwMenu';
import { Item } from '../TypeStage/StageComponent/MbwStageComponent/MbwMenuConstructor';

const withMenuComponent = (
  MenuComponent: React.FC<UiComponentElementInterface<StageComponentInterface> & StageElementInterface>
): React.FC<StageElementInterface> => ({ className, uiComponent }) => {
  const [openCustomFullScreen, setOpenCustomFullScreen] = useState(false);
  const [videoHeight, setVideoHeight] = useState(0);
  const [selectedMenuItem, setMenuItem] = useState<Item | undefined>(undefined);
  const [currentTime, setCurrentTime] = useState<number>();
  const [isPlaying, setIsPlaying] = useState<boolean>();
  const videoRef = useRef<HTMLDivElement>(null);
  const { isAppView } = useMobileApp();

  // The whole component will always take the initial video height
  function onResize() {
    if (!videoRef.current || selectedMenuItem) return;
    setVideoHeight(videoRef.current?.offsetHeight);
  }
  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [videoRef.current]);

  const onCustomFullScreen = (time?: number, _isPlaying?: boolean) => {
    setOpenCustomFullScreen(!openCustomFullScreen);
    setCurrentTime(time);
    setIsPlaying(_isPlaying);
    if (!openCustomFullScreen) onResize();
  };

  const generalStageVideoElement = (_onCustomFullScreen?: (time?: number, isPlaying?: boolean) => void, isFullScreen?: boolean) => (
    <GeneralStageVideo
      onClick={() => setMenuItem(undefined)}
      style={{
        background: '#2a2a2a',
        ...(isFullScreen ? { height: '100%' } : { paddingTop: '56.25%' }),
        width: '100%',
        position: 'relative',
      }}
      playerStyle={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }}
      className="flex justify-center items-center"
      stageSettings={uiComponent.stageSettings!}
      iFrameStyle={{
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        width: '100%',
        height: '100%',
      }}
      uiComponent={uiComponent}
      onCustomFullScreen={_onCustomFullScreen}
      isFullScreen={isFullScreen}
      openCustomFullScreen={openCustomFullScreen}
      currentTime={currentTime}
      isPlaying={isPlaying}
    />
  );

  const menuComponentElement = (
    <MenuComponent
      uiComponent={uiComponent}
      videoHeight={openCustomFullScreen ? 500 : videoHeight}
      fullScreen={openCustomFullScreen}
      setMenuItem={setMenuItem}
      selectedMenuItem={selectedMenuItem}
    />
  );

  return (
    <div className={`flex flex-col md:flex-row md:space-x-2 overflow-hidden w-full justify-center ${className ?? ''}`}>
      {!uiComponent.stageSettings?.streamUrl &&
      !uiComponent.stageSettings?.livestream &&
      !uiComponent.stageSettings?.iFrame &&
      uiComponent.stageSettings?.streamType !== 'vystemStudio' ? (
        <div className="w-full bg-black">
          <ReactPlayer
            playsinline={isAppView()}
            style={{ margin: 'auto' }}
            width="100%"
            height="100%"
            playing
            loop
            muted
            url="https://stream.mux.com/Sqi02G28611pVJ83ZRAflX1te2200p31Jng01WWxNr00pHY.m3u8"
          />
        </div>
      ) : (
        <>
          {(uiComponent.stageSettings.streamType === 'Youtube' ||
            uiComponent.stageSettings.streamType === 'Vimeo' ||
            uiComponent.stageSettings.streamType === 'url' ||
            uiComponent.stageSettings.streamType === 'vystemvideo' ||
            uiComponent.stageSettings.streamType === 'Vystemstream' ||
            uiComponent.stageSettings.streamType === 'vystemStudio' ||
            uiComponent.stageSettings.streamType === 'iFrame' ||
            uiComponent.stageSettings.streamType === 'media' ||
            uiComponent.stageSettings.streamType === 'videourl') && (
            <div ref={videoRef} className="h-min" style={{ width: '100%', position: 'relative', height: 'min-content' }}>
              {generalStageVideoElement(onCustomFullScreen)}
            </div>
          )}
        </>
      )}
      {menuComponentElement}
      <PollNotification />
      <CustomFullScreenStage
        onClose={() => setOpenCustomFullScreen(false)}
        open={openCustomFullScreen}
        generalStageVideoElement={generalStageVideoElement(onCustomFullScreen, openCustomFullScreen)}
        menuComponentElement={menuComponentElement}
      />
    </div>
  );
};

interface StageElementInterface {
  className?: string;
  uiComponent: StageComponentInterface;
  videoHeight?: number;
  fullScreen?: boolean;
  setMenuItem?: (item: Item | undefined) => void;
  selectedMenuItem?: Item | undefined;
}

const MbwMenuStage = withMenuComponent(MbwMenu);
export const MbwStage = ({ uiComponent, videoHeight, fullScreen, selectedMenuItem, setMenuItem }: StageElementInterface) => (
  <MbwMenuStage uiComponent={uiComponent} fullScreen={fullScreen} videoHeight={videoHeight} setMenuItem={setMenuItem} selectedMenuItem={selectedMenuItem} />
);

export default MbwStage;
